import { z } from 'zod';

// --- Repository Resolution ---

export const RepoResolutionSchema = z.discriminatedUnion('resolved', [
  z.object({
    resolved: z.literal(true),
    repoSlug: z.string(),
    confidence: z.enum(['high', 'medium', 'low']),
    reasoning: z.string(),
  }),
  z.object({
    resolved: z.literal(false),
    candidates: z.array(z.string()).max(5),
    clarifyingQuestion: z.string(),
    reasoning: z.string(),
  }),
]);
export type RepoResolution = z.infer<typeof RepoResolutionSchema>;

// --- Persona Resolution ---

/**
 * LLM-backed persona resolution for inbound Slack tasks. Mirrors the shape of
 * {@link RepoResolutionSchema}: either the LLM confidently picks one persona,
 * or it returns a short list of candidates for an interactive picker.
 *
 * - `resolved: true, confidence: 'high'` — auto-select the persona and skip
 *   the interactive picker. The Slack handler will move straight to the
 *   Proceed/Cancel confirmation.
 * - `resolved: true, confidence: 'medium'` — treat as ambiguous: show the
 *   picker with the matched persona pinned first plus fallback candidates.
 * - `resolved: false` with non-empty `candidates` — show the interactive
 *   persona picker with a clarifying question.
 * - `resolved: false` with empty `candidates` — no persona fits; fall through
 *   to the existing GitHub repo resolution flow.
 */
export const PersonaResolutionSchema = z.discriminatedUnion('resolved', [
  z.object({
    resolved: z.literal(true),
    personaId: z.string(),
    personaName: z.string(),
    confidence: z.enum(['high', 'medium']),
    reasoning: z.string(),
  }),
  z.object({
    resolved: z.literal(false),
    candidates: z.array(z.object({ id: z.string(), name: z.string() })).max(5),
    clarifyingQuestion: z.string(),
    reasoning: z.string(),
  }),
]);
export type PersonaResolution = z.infer<typeof PersonaResolutionSchema>;

// --- Task Actionability ---

/**
 * LLM-backed triage for an inbound Slack @reflex mention: does the ask
 * actually warrant launching an agent persona, or is it conversational
 * (a question, a thank-you, a status check) that should just be answered
 * in-thread?
 *
 * This runs *before* persona/repo resolution so a plain question no longer
 * automatically spins up an agent devbox.
 *
 * - `needsAgent: true` — the ask is an actionable coding/automation task;
 *   continue into the persona resolution + confirmation flow as before.
 * - `needsAgent: false` — the ask is conversational; post `reply` in the
 *   thread and launch nothing. `reply` may be empty, in which case the
 *   caller substitutes a generic nudge.
 *
 * `reply` is included on both branches (empty on the `true` branch) so the
 * schema stays a flat object: an LLM that returns a stray `reply` alongside
 * `needsAgent: true` still parses, and a `needsAgent: false` result that
 * forgot `reply` still parses via the default. Classification failures fall
 * open to `needsAgent: true` so the feature can never silently stop agents
 * from launching.
 */
export const TaskActionabilitySchema = z.object({
  needsAgent: z.boolean(),
  reply: z.string().default(''),
  reasoning: z.string().default(''),
});
export type TaskActionability = z.infer<typeof TaskActionabilitySchema>;

// --- Intention Synthesis ---

/**
 * LLM-synthesized launch "intention" for an inbound Slack task when the org
 * has no agent personas configured. Rather than hard-stopping, the Slack
 * handler resolves a repo and launches a generic agent framed by a
 * `systemPrompt` synthesized from the message.
 *
 * `systemPrompt` is the agent's synthesized role/framing; the user's original
 * message is still passed as the task prompt. Synthesis failures (a disabled
 * or erroring LLM, or an empty/invalid response) fall back to a static
 * baseline system prompt so a launch is never blocked — mirroring how
 * {@link TaskActionabilitySchema} fails open.
 */
export const IntentionSynthesisSchema = z.object({
  systemPrompt: z.string(),
  reasoning: z.string().default(''),
});
export type IntentionSynthesis = z.infer<typeof IntentionSynthesisSchema>;

// --- Prompt Analysis ---

export const PromptAnalysisSchema = z.object({
  title: z.string(),
  complexity: z.enum(['simple', 'moderate', 'complex']),
  estimatedImpact: z.object({
    systems: z.array(z.string()),
    riskLevel: z.enum(['low', 'medium', 'high']),
    requiresPlanVerification: z.boolean(),
  }),
  reasoning: z.string(),
});
export type PromptAnalysis = z.infer<typeof PromptAnalysisSchema>;
